// Roll-your-own error monitoring — client side.
//
// Fire-and-forget reporter that ships uncaught frontend errors to the public
// ingestion endpoint (POST /public/errors, anonymous + rate-limited).
// Mirrors the raw-fetch + keepalive + silent-catch pattern used by the Meta
// pixel beacon (utils/metaPixel.ts) — public endpoints are called with plain
// fetch, NOT the authed axios wrapper.
//
// Design rules:
//   * Every exported function is wrapped so the reporter can NEVER throw and
//     take the app down with it.
//   * Chunk-load failures are skipped — the router already self-heals those
//     with a one-time reload (router/index.ts). We replicate the matcher here
//     rather than importing it, to avoid a circular dependency on the router.
//   * Opaque cross-origin "Script error." noise is dropped.
//   * Client-side throttle keeps a runaway loop from spamming the endpoint.

import type { App } from 'vue'
import { getActivePinia } from 'pinia'
import { useWorkspaceStore } from '@/stores/workspace'
import { useAuthStore } from '@/stores/auth'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.staging.profitjam.io'

type ErrorSource = 'frontend' | 'builder'

interface ReportOpts {
  source?: ErrorSource
  component?: string
  extra?: Record<string, unknown>
}

interface NormalizedError {
  message: string
  stack?: string
}

// ---------------------------------------------------------------------------
// Skip filters
// ---------------------------------------------------------------------------

// Replicated from router/index.ts isChunkLoadFailure() — kept in sync manually
// to avoid importing the router (circular). These are already recovered by a
// one-time reload, so reporting them is noise.
function isChunkLoadFailure(message: string): boolean {
  if (!message) return false
  return (
    message.includes('Failed to fetch dynamically imported module')
    || message.includes('error loading dynamically imported module')
    || message.includes('Importing a module script failed')
    || /Loading (chunk|CSS chunk) \d+ failed/.test(message)
    || (message.includes('MIME type') && message.includes('text/html'))
  )
}

// Cross-origin scripts surface uncaught errors as the opaque "Script error."
// with no stack — nothing actionable, so drop it.
function isScriptErrorNoise(message: string, stack?: string): boolean {
  const m = message.trim()
  return (m === 'Script error.' || m === 'Script error') && !stack
}

// ---------------------------------------------------------------------------
// Throttle: same fingerprint max 3 / page session, global cap 20 / minute
// ---------------------------------------------------------------------------

const SESSION_MAX_PER_FINGERPRINT = 3
const GLOBAL_MAX_PER_MINUTE = 20

const fingerprintCounts = new Map<string, number>()
let minuteWindowStart = 0
let minuteCount = 0

function shouldSend(fingerprint: string): boolean {
  const now = Date.now()
  if (now - minuteWindowStart >= 60_000) {
    minuteWindowStart = now
    minuteCount = 0
  }
  if (minuteCount >= GLOBAL_MAX_PER_MINUTE) return false
  const seen = fingerprintCounts.get(fingerprint) || 0
  if (seen >= SESSION_MAX_PER_FINGERPRINT) return false
  fingerprintCounts.set(fingerprint, seen + 1)
  minuteCount += 1
  return true
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function normalizeError(input: unknown): NormalizedError {
  // Promise rejections — unwrap the reason.
  if (typeof PromiseRejectionEvent !== 'undefined' && input instanceof PromiseRejectionEvent) {
    return normalizeError(input.reason)
  }
  // window 'error' events — prefer the real Error, fall back to the message.
  if (typeof ErrorEvent !== 'undefined' && input instanceof ErrorEvent) {
    if (input.error) return normalizeError(input.error)
    return { message: input.message || 'Unknown error' }
  }
  if (input instanceof Error) {
    return { message: input.message || input.name || 'Error', stack: input.stack }
  }
  if (typeof input === 'string') {
    return { message: input }
  }
  // Error-like objects (some libs throw plain objects with message/stack).
  const anyIn = input as { message?: unknown; stack?: unknown } | null
  if (anyIn && typeof anyIn.message === 'string') {
    return {
      message: anyIn.message,
      stack: typeof anyIn.stack === 'string' ? anyIn.stack : undefined,
    }
  }
  try {
    return { message: String(input) }
  } catch {
    return { message: 'Unknown error' }
  }
}

function firstStackLine(stack?: string): string {
  if (!stack) return ''
  const lines = stack.split('\n')
  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed.startsWith('at ') || trimmed.includes('@')) return trimmed
  }
  return lines[0]?.trim() || ''
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

// ---------------------------------------------------------------------------
// Context (workspace + member) from Pinia, with localStorage fallback
// ---------------------------------------------------------------------------

function getContext(): { workspaceId?: string | number; memberId?: string } {
  const ctx: { workspaceId?: string | number; memberId?: string } = {}
  try {
    const pinia = getActivePinia()
    if (pinia) {
      try {
        const workspace = useWorkspaceStore(pinia)
        if (workspace?.currentWorkspaceId != null) ctx.workspaceId = workspace.currentWorkspaceId
      } catch { /* store not ready */ }
      try {
        const auth = useAuthStore(pinia)
        if (auth?.user?.id) ctx.memberId = auth.user.id
      } catch { /* store not ready */ }
    }
  } catch { /* pinia not initialized */ }
  if (!ctx.memberId) {
    try {
      const raw = localStorage.getItem('auth_user')
      const parsed = raw ? JSON.parse(raw) : null
      if (parsed?.id) ctx.memberId = parsed.id
    } catch { /* ignore */ }
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Send
// ---------------------------------------------------------------------------

function send(payload: NormalizedError & ReportOpts): void {
  try {
    const body: Record<string, unknown> = {
      message: truncate(payload.message, 2000),
      source: payload.source || 'frontend',
    }
    if (payload.stack) body.stack = truncate(payload.stack, 8000)
    try { body.url = location.href } catch { /* ignore */ }

    const buildHash = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : undefined
    const version = typeof __PJ_VERSION__ !== 'undefined' ? __PJ_VERSION__ : undefined
    if (buildHash) body.buildHash = buildHash
    if (version) body.version = version

    if (payload.component) body.component = payload.component
    if (payload.extra) body.extra = payload.extra

    const ctx = getContext()
    if (ctx.workspaceId != null) body.workspaceId = ctx.workspaceId
    if (ctx.memberId) body.memberId = ctx.memberId

    fetch(`${API_URL}/public/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // Silent fail — never surface a monitoring hiccup to the user.
    })
  } catch {
    // Never throw from the reporter.
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Report an error of any shape (Error, string, event, error-like object).
 * Skips chunk-load failures and opaque script-error noise, throttles, and
 * ships to the public endpoint. Safe to call from anywhere — never throws.
 */
export function reportError(err: unknown, opts: ReportOpts = {}): void {
  try {
    const { message, stack } = normalizeError(err)
    if (!message) return
    if (isChunkLoadFailure(message)) return
    if (isScriptErrorNoise(message, stack)) return

    const fingerprint = `${message}::${firstStackLine(stack)}`
    if (!shouldSend(fingerprint)) return

    send({
      message,
      stack,
      source: opts.source || 'frontend',
      component: opts.component,
      extra: opts.extra,
    })
  } catch {
    // The reporter must never throw.
  }
}

// Shared window listeners used by both the main app and the builder iframe.
function attachWindowListeners(win: Window, source: ErrorSource): void {
  try {
    win.addEventListener(
      'error',
      (event: ErrorEvent) => {
        try {
          // Resource-load failures (img/script/link that failed to load) also
          // fire an 'error' event, on the element, with no `.error`. Skip them.
          const target = event.target as (EventTarget & { nodeType?: number }) | null
          if (!event.error && target && target !== win && target.nodeType === 1) return
          reportError(event.error || event.message || event, { source })
        } catch { /* never throw from a listener */ }
      },
      true,
    )
    win.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        reportError(event.reason ?? event, { source })
      } catch { /* never throw from a listener */ }
    })
  } catch { /* addEventListener unavailable */ }
}

/**
 * Wire the main Vue app: a config.errorHandler (reports with the component
 * name + Vue `info` string, then preserves Vue's default console visibility)
 * plus window-level `error` / `unhandledrejection` listeners.
 */
export function installGlobalErrorHooks(app: App): void {
  try {
    app.config.errorHandler = (err, instance, info) => {
      try {
        const options = (instance as unknown as { $options?: { name?: string; __name?: string } })?.$options || {}
        const component = options.name || options.__name || undefined
        reportError(err, {
          source: 'frontend',
          component,
          extra: info ? { info } : undefined,
        })
      } catch { /* never throw from the handler */ }
      // Preserve Vue's default behavior — the default handler logs the error,
      // so we do too rather than silently swallowing it.
      // eslint-disable-next-line no-console
      console.error(err)
    }
    attachWindowListeners(window, 'frontend')
  } catch { /* never break bootstrap */ }
}

/**
 * Wire an arbitrary window (the builder canvas iframe) with the same two
 * window listeners, reporting under the given source. The iframe is a second
 * Vue realm that top-window listeners don't reliably cover, so it needs its
 * own hooks.
 */
export function installWindowErrorHooks(win: Window, source: 'builder'): void {
  attachWindowListeners(win, source)
}
